/**
 * FDM Analysis Tab Component
 * Analysis rules, actions, and execution history
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Target, Settings, Play, AlertTriangle, Database, 
  CheckCircle2, Clock, Eye, Search, Activity, Globe, ArrowUpRight
} from 'lucide-react';

export default function FDMAnalysisTab({ 
  analysisRules,
  ruleExecutions,
  analyzing,
  onSeedDefaultRules,
  onRunBatchAnalysis,
  onShowRuleDialog,
  onToggleRuleStatus
}) {
  return (
    <div className="space-y-6 mt-6">
      {/* Analysis Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Active Rules</p>
                <p className="text-2xl font-bold text-slate-100">{analysisRules.filter(r => r.status === 'active').length}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/20">
                <Target className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">System Rules</p>
                <p className="text-2xl font-bold text-slate-100">{analysisRules.filter(r => r.is_system).length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20">
                <Settings className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Executions (24h)</p>
                <p className="text-2xl font-bold text-slate-100">{ruleExecutions.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/20">
                <Play className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Incidents Created</p>
                <p className="text-2xl font-bold text-slate-100">{ruleExecutions.filter(e => e.incident_created_id).length}</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500/20">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analysis Rules */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg text-slate-100">Analysis Rules</CardTitle>
              <CardDescription>Automated rules for exceedance detection and incident creation</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onSeedDefaultRules} data-testid="seed-rules-btn">
                <Database className="w-4 h-4 mr-2" />
                Seed Defaults
              </Button>
              <Button variant="outline" size="sm" onClick={onRunBatchAnalysis} disabled={analyzing} data-testid="run-analysis-btn">
                <Play className="w-4 h-4 mr-2" />
                {analyzing ? 'Analyzing...' : 'Run Analysis'}
              </Button>
              <Button onClick={onShowRuleDialog} data-testid="add-rule-btn">
                <Target className="w-4 h-4 mr-2" />
                Add Rule
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {analysisRules.length > 0 ? (
            <div className="space-y-2">
              {analysisRules.map(rule => (
                <div key={rule.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-700/30">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${rule.status === 'active' ? 'bg-green-500/20' : 'bg-slate-600/30'}`}>
                      <Target className={`w-5 h-5 ${rule.status === 'active' ? 'text-green-400' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-200">{rule.name}</p>
                        {rule.is_system && <Badge variant="outline" className="text-xs">System</Badge>}
                        <Badge className={rule.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}>
                          {rule.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {rule.trigger_type?.replace('_', ' ')} • Min: {rule.min_severity?.replace('_', ' ')} • Priority: {rule.priority}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      {(rule.actions || []).slice(0, 3).map((action, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {action.replace('_', ' ').replace('flag for ', '').replace('notify ', '').slice(0, 10)}
                        </Badge>
                      ))}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => onToggleRuleStatus(rule.id, rule.status)}
                      disabled={rule.is_system}
                    >
                      {rule.status === 'active' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                      ) : (
                        <Clock className="w-4 h-4 text-slate-400" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Target className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400">No analysis rules configured</p>
              <p className="text-xs text-slate-500 mt-1">Click "Seed Defaults" to load standard aviation safety rules</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rule Actions Reference */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg text-slate-100">Available Actions</CardTitle>
          <CardDescription>Actions that can be triggered by analysis rules</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { id: 'flag_for_review', name: 'Flag for Review', icon: Eye, color: 'text-blue-400', desc: 'Mark for safety team review' },
              { id: 'flag_for_investigation', name: 'Flag for Investigation', icon: Search, color: 'text-orange-400', desc: 'Requires investigation' },
              { id: 'create_incident', name: 'Create Incident', icon: AlertTriangle, color: 'text-red-400', desc: 'Auto-create incident report' },
              { id: 'notify_safety_team', name: 'Notify Safety Team', icon: Activity, color: 'text-green-400', desc: 'Alert safety managers' },
              { id: 'notify_management', name: 'Notify Management', icon: Globe, color: 'text-purple-400', desc: 'Escalate to management' },
              { id: 'escalate', name: 'Escalate', icon: ArrowUpRight, color: 'text-rose-400', desc: 'Immediate escalation' }
            ].map(action => (
              <div key={action.id} className="p-3 rounded-lg bg-slate-700/30">
                <div className="flex items-center gap-2">
                  <action.icon className={`w-4 h-4 ${action.color}`} />
                  <span className="text-sm font-medium text-slate-200">{action.name}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{action.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Execution History */}
      {ruleExecutions.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-slate-100">Recent Rule Executions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {ruleExecutions.slice(0, 10).map((exec, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/20">
                  <div className="flex items-center gap-3">
                    <Play className={`w-4 h-4 ${exec.triggered ? 'text-green-400' : 'text-slate-400'}`} />
                    <div>
                      <p className="text-sm text-slate-200">{exec.rule_name}</p>
                      <p className="text-xs text-slate-500">{new Date(exec.executed_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {exec.incident_created_id && (
                      <Badge className="bg-red-500/20 text-red-400">Incident Created</Badge>
                    )}
                    {exec.notifications_sent > 0 && (
                      <Badge variant="secondary">{exec.notifications_sent} notifications</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
